// HomePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from './i18n';
import {
  ChevronRight, Globe, Mic, Wrench, Users, Clock, Award, ArrowRight,
  Menu, X, CheckCircle, Target, Zap, Shield, Gamepad2, Volume2,
  TestTube, Sparkles, Languages, Star, Play, GitBranch, Cpu,
  FileText, Headphones, Brain, BarChart, Settings, Database,
  Layers, Monitor, MessageSquare, TrendingUp, Code, Palette, UserCheck
} from 'lucide-react';
import './home_page.css';

const HomePage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [activeService, setActiveService] = useState(0);
  const [activeTab, setActiveTab] = useState('tep');
  const [activeTool, setActiveTool] = useState(0);
  const navigate = useNavigate();
  const { t: __t, get, lang, changeLanguage } = useI18n();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 语言内容配置
  const content = {
    zh: {
      nav: {
        services: "服务",
        about: "关于我们",
        workflow: "工作流程",
        technology: "技术优势",
        contact: "联系我们",
        getQuote: "获取报价"
      },
      hero: {
        title: "Games Localization Studio",
        subtitle: "融合人工创意与AI技术的力量，为全球游戏提供情感共鸣的本地化体验",
        pressStart: "开始体验",
        viewSamples: "查看案例",
        stats: {
          projects: "500+",
          projectsLabel: "成功项目",
          languages: "14+",
          languagesLabel: "支持语言",
          satisfaction: "99%",
          satisfactionLabel: "客户满意度",
          delivery: "24/7",
          deliveryLabel: "全球服务"
        }
      },
      about: {
        title: "不只是本地化，是文化的桥梁",
        subtitle: "我们帮助全球游戏开发者触达国际玩家，与他们建立本地化的情感连接",
        paragraph1: "PinkPin不仅仅是另一家本地化公司。我们提供一系列技术和本地化服务，帮助全球客户触达国际消费者群体，与他们进行本地化互动，并实现工作流程创新。",
        paragraph2: "我们的东京办公室专门从事日语配音，与业内顶尖声优和导演合作。通过专业的录音团队和先进的技术设备，我们为日本及全球玩家带来生动的角色演绎。",
        features: [
          { title: "专职专家团队", desc: "全职雇用各垂直市场的主题专家" },
          { title: "AI增强技术", desc: "人工创意与AI技术的完美融合" },
          { title: "全球网络", desc: "亚洲、欧洲、美洲24小时服务" },
          { title: "行业认证", desc: "ISO认证的质量管理体系" }
        ]
      },
      textLoc: {
        title: "文本本地化",
        subtitle: "精准传达游戏的灵魂",
        description: "我们的文本本地化服务不只是翻译文字，而是重新创造能够引起目标玩家情感共鸣的内容。",
        tabs: {
          tep: {
            title: "TEP服务",
            subtitle: "传统翻译-编辑-校对",
            content: "由母语专家组成的专业团队，提供三步质量保证流程。每个项目都经过资深译员翻译、编辑润色和最终校对，确保语言的准确性和文化适配性。",
            features: ["游戏专业术语库管理", "角色语音风格保持", "文化敏感内容审查", "多平台格式支持"]
          },
          aipe: {
            title: "AIPE服务",
            subtitle: "AI增强本地化引擎",
            content: "结合最新的AI技术和人工专业知识，提供更快速、更一致的本地化服务。我们的专有AI模型经过大量游戏文本训练，能够理解游戏特定的语境和风格。",
            features: ["游戏专用AI模型", "实时翻译记忆库", "智能术语管理", "自动化质量检查"]
          },
          process: {
            title: "工作流程",
            subtitle: "端到端解决方案",
            content: "从项目启动到最终交付，我们提供完整的项目管理服务。专门的项目经理全程跟进，确保按时交付高质量成果。",
            features: ["项目评估与报价", "资源分配与时间规划", "进度追踪与汇报", "售后支持与更新"]
          }
        }
      },
      voiceOver: {
        title: "配音服务",
        subtitle: "让角色活起来",
        description: "东京专业录音棚，汇聚日本顶级声优资源",
        features: [
          { title: "顶级声优阵容", items: ["与知名声优事务所直接合作", "500+专业声优资源库", "动画、游戏配音经验丰富"] },
          { title: "专业录音设施", items: ["东京都内专业录音棚", "Dolby Atmos音频制作", "实时远程监听系统"] },
          { title: "全流程服务", items: ["剧本翻译与改编", "声优选角与试音", "录音监制与后期制作"] }
        ],
        process: {
          title: "配音工作流程",
          steps: [
            { phase: "前期准备", tasks: ["剧本分析", "角色定位", "声优选择"] },
            { phase: "录音制作", tasks: ["专业导演指导", "实时质量监控", "多轨录制"] },
            { phase: "后期处理", tasks: ["音频编辑", "音效合成", "最终母带"] }
          ]
        }
      },
      culturalization: {
        title: "文化适配",
        subtitle: "深度本地化，避免文化风险",
        description: "不仅是语言的转换，更是文化的重塑",
        services: [
          { title: "文化风险评估", desc: "识别并规避可能引起争议的内容，包括宗教、政治、历史等敏感元素" },
          { title: "视觉内容调整", desc: "符号、图标、颜色的文化含义审查，确保视觉元素的文化适当性" },
          { title: "叙事本地化", desc: "调整故事情节、角色设定，使其符合目标市场的文化期待和价值观" },
          { title: "用户体验优化", desc: "根据不同地区玩家习惯，调整游戏机制、难度曲线和奖励系统" }
        ]
      },
      tools: {
        title: "本地化工具套件",
        subtitle: "智能化工具链，革新本地化工作流程",
        description: "自主研发的工具平台，提升效率降低成本",
        items: [
          {
            title: "版本管理系统",
            subtitle: "定制化本地化版本管理工具",
            features: ["多语言版本并行管理", "增量更新与回滚机制", "自动化构建与部署", "版本差异对比分析"]
          },
          {
            title: "AI翻译引擎",
            subtitle: "游戏专用AI翻译工具",
            features: ["游戏领域专用模型", "上下文智能识别", "术语一致性保证", "实时质量评分"]
          },
          {
            title: "资源管理平台",
            subtitle: "集中化资源管理",
            features: ["翻译记忆库管理", "术语库维护系统", "参考资料存储", "团队协作空间"]
          },
          {
            title: "质量控制系统",
            subtitle: "自动化质量保证",
            features: ["自动化测试脚本", "语言质量检查", "UI/UX验证工具", "错误追踪系统"]
          }
        ]
      },
      lqa: {
        title: "LQA测试服务",
        subtitle: "确保完美的玩家体验",
        description: "多层次、全方位的质量保证体系",
        levels: [
          { title: "语言测试", items: ["拼写与语法检查", "术语一致性验证", "语言流畅度评估", "文化适当性审查"] },
          { title: "功能测试", items: ["UI文本显示验证", "字符编码测试", "文本溢出检查", "功能完整性验证"] },
          { title: "游戏测试", items: ["游戏流程测试", "剧情连贯性检查", "音画同步验证", "多人游戏测试"] }
        ],
        coverage: {
          title: "测试覆盖范围",
          platforms: ["PC", "Console", "Mobile", "VR/AR"],
          types: ["AAA游戏", "独立游戏", "手游", "网页游戏"]
        }
      },
      workflow: {
        title: "工作流程",
        subtitle: "透明高效的项目管理",
        steps: [
          { number: "01", title: "需求分析", desc: "深入了解游戏内容、目标市场和本地化需求" },
          { number: "02", title: "方案制定", desc: "定制化本地化策略、资源配置和时间规划" },
          { number: "03", title: "执行实施", desc: "专业团队并行作业，确保高效推进" },
          { number: "04", title: "质量控制", desc: "多轮测试与审核，确保零缺陷交付" },
          { number: "05", title: "交付支持", desc: "持续的售后支持和内容更新服务" }
        ]
      },
      technology: {
        title: "技术优势",
        subtitle: "人工智能与人类创意的完美融合",
        items: [
          { title: "专有AI模型", desc: "基于海量游戏文本训练的专用模型" },
          { title: "效率提升60%", desc: "AI辅助下的本地化效率大幅提升" },
          { title: "质量保证99.9%", desc: "多重质量控制确保近乎完美的输出" },
          { title: "24小时交付", desc: "紧急项目的快速响应能力" }
        ]
      },
      cta: {
        title: "准备好征服全球市场了吗？",
        subtitle: "让我们帮助您的游戏在全球获得成功",
        getQuote: "获取报价",
        consultation: "免费咨询",
        downloadBrochure: "下载服务手册"
      },
      footer: {
        services: "服务",
        company: "公司",
        contact: "联系方式",
        servicesItems: ["文本本地化", "配音服务", "文化适配", "本地化工具", "LQA测试"],
        companyItems: ["关于我们", "成功案例", "团队介绍", "新闻动态"],
        rights: "© 2024 Pinkpin Games Localization Studio. 版权所有。"
      }
    },
    en: {
      nav: {
        services: "Services",
        about: "About",
        workflow: "Workflow",
        technology: "Technology",
        contact: "Contact",
        getQuote: "Get Quote"
      },
      hero: {
        title: "Games Localization Studio",
        subtitle: "Blending human creativity with AI technology to deliver emotionally resonant localization experiences",
        pressStart: "Press Start",
        viewSamples: "View Samples",
        stats: {
          projects: "500+",
          projectsLabel: "Successful Projects",
          languages: "14+",
          languagesLabel: "Languages Supported",
          satisfaction: "99%",
          satisfactionLabel: "Client Satisfaction",
          delivery: "24/7",
          deliveryLabel: "Global Service"
        }
      },
      about: {
        title: "Not Just Localization, But Cultural Bridges",
        subtitle: "We help global game developers reach international players and establish localized emotional connections",
        paragraph1: "PinkPin isn't just another localization company. We provide a range of tech and localization services that help our global clients reach their international consumer base.",
        paragraph2: "Our Tokyo office specializes in Japanese voiceover, collaborating with top voice actors and directors in the industry.",
        features: [
          { title: "Expert Teams", desc: "Full-time subject matter experts in key verticals" },
          { title: "AI-Enhanced", desc: "Perfect blend of human creativity and AI technology" },
          { title: "Global Network", desc: "24/7 service across Asia, Europe, and Americas" },
          { title: "Industry Certified", desc: "ISO certified quality management system" }
        ]
      },
      textLoc: {
        title: "Text Localization",
        subtitle: "Accurately Conveying the Soul of Your Game",
        description: "Our text localization service goes beyond translating words - we recreate content that resonates emotionally with target players.",
        tabs: {
          tep: {
            title: "TEP Service",
            subtitle: "Translation-Editing-Proofreading",
            content: "Professional teams of native speakers provide a three-step quality assurance process.",
            features: ["Game terminology management", "Character voice consistency", "Cultural sensitivity review", "Multi-platform format support"]
          },
          aipe: {
            title: "AIPE Service",
            subtitle: "AI-Enhanced Localization Engine",
            content: "Combining cutting-edge AI technology with human expertise to deliver faster, more consistent localization services.",
            features: ["Game-specific AI models", "Real-time translation memory", "Intelligent term management", "Automated quality checks"]
          },
          process: {
            title: "Workflow",
            subtitle: "End-to-End Solutions",
            content: "From project initiation to final delivery, we provide complete project management services.",
            features: ["Project assessment & quotation", "Resource allocation & scheduling", "Progress tracking & reporting", "Post-delivery support"]
          }
        }
      },
      voiceOver: {
        title: "Voice Over Services",
        subtitle: "Bringing Characters to Life",
        description: "Professional Tokyo recording studio with Japan's top voice talent resources",
        features: [
          { title: "Top Voice Talent", items: ["Direct cooperation with renowned agencies", "500+ professional voice actors", "Rich experience in anime and games"] },
          { title: "Professional Recording", items: ["Professional studios in Tokyo", "Dolby Atmos audio production", "Real-time remote monitoring"] },
          { title: "Full Service", items: ["Script translation & adaptation", "Voice casting & auditions", "Recording direction & post-production"] }
        ],
        process: {
          title: "Voice Recording Process",
          steps: [
            { phase: "Pre-production", tasks: ["Script analysis", "Character positioning", "Voice actor selection"] },
            { phase: "Recording", tasks: ["Professional direction", "Real-time quality control", "Multi-track recording"] },
            { phase: "Post-production", tasks: ["Audio editing", "Sound synthesis", "Final mastering"] }
          ]
        }
      },
      culturalization: {
        title: "Culturalization",
        subtitle: "Deep Localization, Avoiding Cultural Risks",
        description: "Not just language conversion, but cultural reshaping",
        services: [
          { title: "Cultural Risk Assessment", desc: "Identify and avoid potentially controversial content" },
          { title: "Visual Content Adjustment", desc: "Review cultural meanings of symbols, icons, and colors" },
          { title: "Narrative Localization", desc: "Adjust storylines to align with target market expectations" },
          { title: "UX Optimization", desc: "Adjust game mechanics based on regional player preferences" }
        ]
      },
      tools: {
        title: "Localization Tool Suite",
        subtitle: "Intelligent Toolchain Revolutionizing Localization Workflows",
        description: "Self-developed tool platform to improve efficiency and reduce costs",
        items: [
          {
            title: "Version Management",
            subtitle: "Customized Localization Version Control",
            features: ["Multi-language parallel management", "Incremental updates & rollback", "Automated build & deployment", "Version diff analysis"]
          },
          {
            title: "AI Translation Engine",
            subtitle: "Game-Specific AI Translation Tool",
            features: ["Game domain-specific models", "Context-aware recognition", "Terminology consistency", "Real-time quality scoring"]
          },
          {
            title: "Resource Platform",
            subtitle: "Centralized Resource Management",
            features: ["Translation memory management", "Terminology database", "Reference materials storage", "Team collaboration space"]
          },
          {
            title: "Quality Control",
            subtitle: "Automated Quality Assurance",
            features: ["Automated test scripts", "Language quality checks", "UI/UX validation tools", "Bug tracking system"]
          }
        ]
      },
      lqa: {
        title: "LQA Testing Services",
        subtitle: "Ensuring Perfect Player Experience",
        description: "Multi-level, comprehensive quality assurance system",
        levels: [
          { title: "Language Testing", items: ["Spelling & grammar check", "Terminology consistency", "Language fluency assessment", "Cultural appropriateness"] },
          { title: "Functional Testing", items: ["UI text display verification", "Character encoding test", "Text overflow check", "Functional integrity"] },
          { title: "Game Testing", items: ["Gameplay flow testing", "Story coherence check", "Audio-visual sync", "Multiplayer testing"] }
        ],
        coverage: {
          title: "Testing Coverage",
          platforms: ["PC", "Console", "Mobile", "VR/AR"],
          types: ["AAA Games", "Indie Games", "Mobile Games", "Web Games"]
        }
      },
      workflow: {
        title: "Workflow",
        subtitle: "Transparent and Efficient Project Management",
        steps: [
          { number: "01", title: "Requirements Analysis", desc: "Deep understanding of game content and localization needs" },
          { number: "02", title: "Solution Design", desc: "Customized localization strategy and resource allocation" },
          { number: "03", title: "Execution", desc: "Professional teams working in parallel for efficient progress" },
          { number: "04", title: "Quality Control", desc: "Multiple rounds of testing for zero-defect delivery" },
          { number: "05", title: "Delivery & Support", desc: "Continuous post-delivery support and content updates" }
        ]
      },
      technology: {
        title: "Technology Advantages",
        subtitle: "Perfect Fusion of AI and Human Creativity",
        items: [
          { title: "Proprietary AI Models", desc: "Specialized models trained on massive game text" },
          { title: "60% Efficiency Boost", desc: "Significant efficiency improvement with AI assistance" },
          { title: "99.9% Quality Assurance", desc: "Multiple quality controls ensure near-perfect output" },
          { title: "24-Hour Delivery", desc: "Fast response capability for urgent projects" }
        ]
      },
      cta: {
        title: "Ready to Conquer Global Markets?",
        subtitle: "Let us help your game succeed worldwide",
        getQuote: "Get Quote",
        consultation: "Free Consultation",
        downloadBrochure: "Download Brochure"
      },
      footer: {
        services: "Services",
        company: "Company",
        contact: "Contact",
        servicesItems: ["Text Localization", "Voice Over", "Culturalization", "Localization Tools", "LQA Testing"],
        companyItems: ["About Us", "Case Studies", "Our Team", "News"],
        rights: "© 2024 Pinkpin Games Localization Studio. All rights reserved."
      }
    }
  };

  const t = get('home:') || {};
  const language = lang;

  // 数字动画组件
  const CountUp = ({ end, duration = 2000, suffix = "" }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      let startTime;
      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }, [end, duration]);

    return <span>{count}{suffix}</span>;
  };

  const languages = get('home:languagesRibbon.items') || [];

  const getIconComponent = (iconName) => {
    const icons = {
      Shield, Palette, MessageSquare, UserCheck,
      GitBranch, Cpu, Database, Monitor,
      FileText, Settings, Gamepad2,
      Brain, TrendingUp, Clock
    };
    return icons[iconName] || Globe;
  };

  return (
    <div className="homepage">
      {/* 装饰元素 */}
      <div className="decorative-elements">
        <div className="emoji emoji-1">🎮</div>
        <div className="emoji emoji-2">🎯</div>
        <div className="emoji emoji-3">🌟</div>
        <div className="emoji emoji-4">🎨</div>
      </div>

      {/* 导航栏 */}
      <nav className={`navbar ${scrollY > 50 ? 'navbar-scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-brand">
            <div className="logo">PINKPIN</div>
            <span className="tagline">{t.hero?.title}</span>
          </div>

          <div className="nav-menu">
            <a href="#services" className="nav-link">{t.nav?.services}</a>
            <a href="#about" className="nav-link">{t.nav?.about}</a>
            <a href="#workflow" className="nav-link">{t.nav?.workflow}</a>
            <a href="#technology" className="nav-link">{t.nav?.technology}</a>
            <a href="#contact" className="nav-link">{t.nav?.contact}</a>

            <button
              onClick={() => changeLanguage(lang === 'zh' ? 'en' : 'zh')}
              className="language-switcher"
            >
              <Languages className="icon-small" />
              <span>{lang === 'zh' ? 'EN' : '中文'}</span>
            </button>

            <button className="btn-primary">
              {t.nav.getQuote}
            </button>
          </div>

          <div className="mobile-menu-buttons">
            <button
              onClick={() => changeLanguage(lang === 'zh' ? 'en' : 'zh')}
              className="mobile-lang-btn"
            >
              <Languages className="icon-small" />
            </button>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="mobile-menu-btn">
              {isMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </nav>

      {/* 移动端菜单 */}
      {isMenuOpen && (
        <div className="mobile-menu">
          <a href="#services" onClick={() => setIsMenuOpen(false)}>{t.nav.services}</a>
          <a href="#about" onClick={() => setIsMenuOpen(false)}>{t.nav.about}</a>
          <a href="#workflow" onClick={() => setIsMenuOpen(false)}>{t.nav.workflow}</a>
          <a href="#technology" onClick={() => setIsMenuOpen(false)}>{t.nav.technology}</a>
          <a href="#contact" onClick={() => setIsMenuOpen(false)}>{t.nav.contact}</a>
        </div>
      )}

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg">
          <div className="bg-blob blob-1"></div>
          <div className="bg-blob blob-2"></div>
        </div>

        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">
                <span className="brand-name">PINKPIN</span>
                <br />
                <span className="subtitle">{t.hero?.title}</span>
              </h1>
              <p className="hero-description">
                {t.hero?.subtitle}
              </p>
              <div className="hero-buttons">
                <button className="btn-primary btn-large" onClick={() => navigate('/login')}>
                  <Play className="icon-small" /> {t.hero?.pressStart}
                </button>
                <button className="btn-outline btn-large">
                  {t.hero?.viewSamples}
                </button>
              </div>

              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-value">
                    <CountUp end={500} suffix="+" />
                  </div>
                  <div className="stat-label">{t.hero?.stats?.projectsLabel}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">14+</div>
                  <div className="stat-label">{t.hero?.stats?.languagesLabel}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    <CountUp end={99} suffix="%" />
                  </div>
                  <div className="stat-label">{t.hero?.stats?.satisfactionLabel}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">24/7</div>
                  <div className="stat-label">{t.hero?.stats?.deliveryLabel}</div>
                </div>
              </div>
            </div>

            <div className="hero-visual">
              <div className="game-device">
                <div className="device-screen">
                    <div className="screen-content">
                    <div
                      className="press-start"
                      role="button"
                      tabIndex={0}
                      onClick={() => navigate('/login')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/login'); }}
                    >
                      {t.hero?.pressStart}
                    </div>
                  </div>
                  <div className="pixel-grid">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="pixel" style={{animationDelay: `${i * 0.1}s`}}></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 语言滚动条 */}
      <section className="languages-ribbon">
        <div className="languages-scroll">
          {[...languages, ...languages].map((lang, index) => (
            <React.Fragment key={index}>
              <span className="language-item">{lang}</span>
              <span className="separator">•</span>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-title">{t.about?.title}</h2>
            <p className="section-subtitle">{t.about?.subtitle}</p>
          </div>

          <div className="about-grid">
            <div>
              <p className="paragraph mb-6">{t.about.paragraph1}</p>
              <p className="paragraph mb-8">{t.about.paragraph2}</p>
            </div>

            <div className="features-grid">
              {t.about?.features?.map((feature, idx) => (
                <div key={idx} className="feature-card">
                  <div className="feature-icon">
                    {idx === 0 && <Users />}
                    {idx === 1 && <Brain />}
                    {idx === 2 && <Globe />}
                    {idx === 3 && <Award />}
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-desc">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-padding bg-gradient">
        <div className="container">
          {/* 文本本地化 */}
          <div className="service-block">
            <div className="text-center mb-12">
              <h2 className="section-title">{t.textLoc?.title}</h2>
              <p className="section-subtitle">{t.textLoc?.subtitle}</p>
              <p className="section-desc">{t.textLoc?.description}</p>
            </div>

            <div className="tabs">
              <div className="tab-nav">
                {Object.keys(t.textLoc?.tabs || {}).map(key => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`tab-btn ${activeTab === key ? 'active' : ''}`}
                  >
                    {t.textLoc?.tabs?.[key]?.title}
                  </button>
                ))}
              </div>

              <div className="tab-content">
                <div className="tab-grid">
                  <div>
                    <h3 className="tab-title">{t.textLoc?.tabs?.[activeTab]?.title}</h3>
                    <p className="tab-subtitle">{t.textLoc?.tabs?.[activeTab]?.subtitle}</p>
                    <p className="tab-desc">{t.textLoc?.tabs?.[activeTab]?.content}</p>
                    <ul className="feature-list">
                      {t.textLoc?.tabs?.[activeTab]?.features?.map((feature, idx) => (
                        <li key={idx}>
                          <CheckCircle className="list-icon" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="tab-visual">
                    <Globe className="visual-icon" />
                    <div className="visual-stats">
                      <div className="visual-stat">
                        <div className="stat-number">14+</div>
                        <div className="stat-text">{t.textLoc?.metrics?.languagesLabel}</div>
                      </div>
                      <div className="visual-stat">
                        <div className="stat-number">99.9%</div>
                        <div className="stat-text">{t.textLoc?.metrics?.accuracyLabel}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 配音服务 */}
          <div className="service-block">
            <div className="text-center mb-12">
              <h2 className="section-title">{t.voiceOver?.title}</h2>
              <p className="section-subtitle">{t.voiceOver?.subtitle}</p>
              <p className="section-desc">{t.voiceOver?.description}</p>
            </div>

            <div className="voice-features">
              {t.voiceOver?.features?.map((feature, idx) => (
                <div key={idx} className="voice-card">
                  <h3 className="card-title">{feature.title}</h3>
                  <ul className="card-list">
                    {feature.items?.map((item, i) => (
                      <li key={i}>
                        <Star className="list-icon-small" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* 文化适配 */}
          <div className="service-block">
            <div className="text-center mb-12">
              <h2 className="section-title">{t.culturalization?.title}</h2>
              <p className="section-subtitle">{t.culturalization?.subtitle}</p>
              <p className="section-desc">{t.culturalization?.description}</p>
            </div>

            <div className="culture-services">
              {t.culturalization?.services?.map((service, idx) => (
                <div key={idx} className="culture-card">
                  <div className="culture-icon">
                    {idx === 0 && <Shield />}
                    {idx === 1 && <Palette />}
                    {idx === 2 && <MessageSquare />}
                    {idx === 3 && <UserCheck />}
                  </div>
                  <h3 className="culture-title">{service.title}</h3>
                  <p className="culture-desc">{service.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 本地化工具 */}
          <div className="service-block">
            <div className="text-center mb-12">
              <h2 className="section-title">{t.tools?.title}</h2>
              <p className="section-subtitle">{t.tools?.subtitle}</p>
              <p className="section-desc">{t.tools?.description}</p>
            </div>

            <div className="tools-grid">
              {t.tools?.items?.map((tool, idx) => (
                <div key={idx} className="tool-card">
                  <div className="tool-header">
                    <div className="tool-icon">
                      {idx === 0 && <GitBranch />}
                      {idx === 1 && <Cpu />}
                      {idx === 2 && <Database />}
                      {idx === 3 && <Monitor />}
                    </div>
                    <div>
                      <h3 className="tool-title">{tool.title}</h3>
                      <p className="tool-subtitle">{tool.subtitle}</p>
                    </div>
                  </div>
                  <ul className="tool-features">
                    {tool.features.map((feature, i) => (
                      <li key={i}>
                        <CheckCircle className="list-icon-small green" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* LQA测试 */}
          <div className="service-block">
            <div className="text-center mb-12">
              <h2 className="section-title">{t.lqa?.title}</h2>
              <p className="section-subtitle">{t.lqa?.subtitle}</p>
              <p className="section-desc">{t.lqa?.description}</p>
            </div>

            <div className="lqa-levels">
              {t.lqa?.levels?.map((level, idx) => (
                <div key={idx} className="lqa-card">
                  <div className="lqa-icon">
                    {idx === 0 && <FileText />}
                    {idx === 1 && <Settings />}
                    {idx === 2 && <Gamepad2 />}
                  </div>
                  <h3 className="lqa-title">{level.title}</h3>
                  <ul className="lqa-list">
                    {level.items.map((item, i) => (
                      <li key={i}>
                        <ChevronRight className="list-arrow" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="coverage-section">
              <h3 className="coverage-title">{t.lqa?.coverage?.title}</h3>
              <div className="coverage-grid">
                <div>
                  <h4 className="coverage-subtitle">{t.lqa?.coverage?.platformLabel}</h4>
                  <div className="tag-list">
                    {t.lqa?.coverage?.platforms?.map((platform, idx) => (
                      <span key={idx} className="tag purple">{platform}</span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="coverage-subtitle">{t.lqa?.coverage?.typeLabel}</h4>
                  <div className="tag-list">
                    {t.lqa?.coverage?.types?.map((type, idx) => (
                      <span key={idx} className="tag pink">{type}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Workflow Section */}
      <section id="workflow" className="section-padding bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-title">{t.workflow?.title}</h2>
            <p className="section-subtitle">{t.workflow?.subtitle}</p>
          </div>

          <div className="workflow-container">
            <div className="workflow-line"></div>
            <div className="workflow-steps">
              {t.workflow?.steps?.map((step, index) => (
                <div key={index} className="workflow-step">
                  <div className="step-number">{step.number}</div>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-desc">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section id="technology" className="section-padding tech-section">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-title text-white">{t.technology?.title}</h2>
            <p className="section-subtitle text-white-80">{t.technology?.subtitle}</p>
          </div>

          <div className="tech-grid">
            {t.technology?.items?.map((item, idx) => (
              <div key={idx} className="tech-card">
                <div className="tech-icon">
                  {idx === 0 && <Brain />}
                  {idx === 1 && <TrendingUp />}
                  {idx === 2 && <Shield />}
                  {idx === 3 && <Clock />}
                </div>
                <h3 className="tech-title">{item.title}</h3>
                <p className="tech-desc">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="contact" className="section-padding cta-section">
        <div className="container">
          <div className="cta-box">
            <h2 className="cta-title">{t.cta?.title}</h2>
            <p className="cta-subtitle">{t.cta?.subtitle}</p>

            <div className="contact-info">
              <a href="mailto:kalmia@pinkpingames.com" className="contact-link pink">
                kalmia@pinkpingames.com
              </a>
              <a href="https://www.pinkpingames.com" className="contact-link purple">
                www.pinkpingames.com
              </a>
            </div>

            <div className="cta-buttons">
              <button className="btn-primary btn-large">
                {t.cta?.getQuote}
              </button>
              <button className="btn-secondary btn-large">
                {t.cta?.consultation}
              </button>
              <button className="btn-outline btn-large">
                {t.cta?.downloadBrochure}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div>
              <div className="footer-logo">PINKPIN</div>
              <p className="footer-tagline">{__t('home:nav.title')}</p>
            </div>

            <div>
              <h3 className="footer-title">{t.footer?.services}</h3>
              <ul className="footer-list">
                {t.footer?.servicesItems?.map((item, idx) => (
                  <li key={idx}><a href="#">{item}</a></li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="footer-title">{t.footer?.company}</h3>
              <ul className="footer-list">
                {t.footer?.companyItems?.map((item, idx) => (
                  <li key={idx}><a href="#">{item}</a></li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="footer-title">{t.footer?.contact}</h3>
              <ul className="footer-list">
                <li>kalmia@pinkpingames.com</li>
                <li>www.pinkpingames.com</li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>{t.footer?.rights}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
